/**
 * №16.1 Stage C — the author canvas container. Owns the template detail + action
 * catalog reads, the selection state, and the ONE patch mutation (every canvas /
 * inspector gesture funnels through it → apply-then-validate → react-query
 * invalidates → re-render). Header carries validate/publish/new-draft + insert
 * block; a derived draft shows its derivation report (D-AU5).
 */
import { useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router";
import "../components/workflow-author/workflow-author.css";
import { showToast } from "../components/primitives";
import {
  useTemplateDetail, useActionCatalog, usePatchTemplate, usePublishTemplate, useNewDraftVersion, useDiscardDraft, useWorkflowBlocks, type WfPatch,
} from "../hooks/use-workflow-templates";
import { TemplateCanvas, type Selection, type TemplateActions } from "../components/workflow-author/TemplateCanvas";
import { InspectorRail, type InspectorCtx } from "../components/workflow-author/InspectorRail";

function DeriveBanner({ report }: { report: string }) {
  return (
    <div className="wfa-findings" style={{ marginBottom: 14 }}>
      <div className="wfa-findings__hd">⌘ Derived draft — the derivation report (nothing runs until published)</div>
      <div style={{ padding: "10px 12px", fontSize: "var(--text-2xs)", color: "var(--color-text-secondary)", whiteSpace: "pre-wrap" }}>{report}</div>
    </div>
  );
}

function BlockPicker({ onClose, onInsert }: { onClose: () => void; onInsert: (code: string) => void }) {
  const { data } = useWorkflowBlocks();
  return (
    <div role="dialog" aria-modal="true" onKeyDown={(e) => e.key === "Escape" && onClose()}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", display: "grid", placeItems: "center", zIndex: 50 }}>
      <div className="wfa" style={{ background: "var(--color-background-primary)", borderRadius: "var(--border-radius-lg)", padding: 18, width: 460, maxWidth: "92vw" }}>
        <h2 style={{ margin: "0 0 4px", fontSize: "var(--text-lg)" }}>Insert a block</h2>
        <div className="wfa-note" style={{ marginTop: 0, marginBottom: 10 }}>Copies the block's published steps into the selected segment, keeping provenance.</div>
        {(data?.blocks ?? []).filter((b) => b.activeVersion > 0).length === 0 && <div className="wfa-empty">No published blocks. Author one from the Workflows list (Blocks tab).</div>}
        {(data?.blocks ?? []).filter((b) => b.activeVersion > 0).map((b) => (
          <button key={b.code} className="wfa-trow" onClick={() => onInsert(b.code)}>
            <div className="wfa-trow__main"><div className="wfa-trow__title">{b.name}</div><div className="wfa-trow__desc">{b.code} · v{b.activeVersion} · {b.stepCount} steps</div></div>
            <div className="wfa-trow__meta"><i className="ti ti-plus" aria-hidden="true" /></div>
          </button>
        ))}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}><button className="wfa-btn ghost" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

export function WorkflowTemplateDetailPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewVersion = searchParams.get("v") ? Number(searchParams.get("v")) : undefined;
  const { data: detail, isLoading, isError, error } = useTemplateDetail(code, viewVersion);
  const { data: catalog } = useActionCatalog();
  const patchMut = usePatchTemplate();
  const publishMut = usePublishTemplate();
  const newDraftMut = useNewDraftVersion();
  const discardMut = useDiscardDraft();

  const [selected, setSelected] = useState<Selection | null>(null);
  const [picked, setPicked] = useState<Record<number, string>>({});
  const [showFindings, setShowFindings] = useState(true);
  const [blockPicker, setBlockPicker] = useState(false);

  const tpl = detail?.template ?? null;
  // №16.1.1 — a draft is only EDITABLE for a maker who holds workflow.author.
  // A non-author sees the full surface read-only + a teaching affordance (never a
  // void). `canAuthor` undefined = still loading → don't prematurely lock.
  const canAuthor = detail?.canAuthor !== false;
  const editable = canAuthor && tpl?.status === "draft";
  const version = tpl?.version ?? 0;

  const patch = (patches: WfPatch[]) => {
    if (!code || !version) return;
    patchMut.mutate({ code, version, patches }, {
      onSuccess: (r) => {
        if (!r.ok) showToast(r.errors[0]?.message ?? "A patch was rejected", "error");
        else if (r.validation.counts.error > 0) showToast(`${r.validation.counts.error} validation error(s) — see the findings panel`, "info");
      },
      onError: (e) => showToast((e as Error).message, "error"),
    });
  };

  const actions: TemplateActions = { patch, busy: patchMut.isPending, editable: !!editable, notify: (m) => showToast(m, "info") };
  const inspectorCtx: InspectorCtx | null = useMemo(() => detail ? { detail, patch, busy: patchMut.isPending, catalog, onSelect: setSelected, onListBlocks: () => setBlockPicker(true) } : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detail, catalog, patchMut.isPending]);

  if (isLoading) return <div className="wfa wfa-list"><div className="wfa-empty">Loading template…</div></div>;
  if (isError || !detail?.found || !tpl) return <div className="wfa wfa-list"><div className="wfa-empty" style={{ color: "var(--color-text-danger)" }}>{(error as Error)?.message ?? "Template not found."}</div></div>;

  const v = detail.validation;
  const versions = detail.versions ?? [];
  const draftV = versions.find((x) => x.status === "draft")?.version;
  const activeV = versions.find((x) => x.status === "active")?.version;
  const goVersion = (n?: number) => setSearchParams(n ? { v: String(n) } : {}, { replace: false });
  const doPublish = () => code && publishMut.mutate({ code, version }, { onSuccess: () => { showToast("Published → Active", "success"); goVersion(version); }, onError: (e) => showToast((e as Error).message, "error") });
  const doNewDraft = () => code && newDraftMut.mutate({ code, from_version: version }, { onSuccess: (r) => { showToast(`Draft v${r.version} opened`, "success"); goVersion(r.version); }, onError: (e) => showToast((e as Error).message, "error") });
  const doDiscard = () => {
    if (!code) return;
    if (!window.confirm(`Discard draft v${version}? Its edits are deleted and can't be recovered.${activeV ? ` You'll return to the Active version (v${activeV}).` : ""}`)) return;
    discardMut.mutate({ code, version }, {
      onSuccess: (r) => { showToast(`Draft v${version} discarded`, "success"); setSelected(null); if (r.active_version) goVersion(r.active_version); else navigate("/workflows"); },
      onError: (e) => showToast((e as Error).message, "error"),
    });
  };
  const insertBlock = (blockCode: string) => {
    if (selected?.segmentNo == null) { showToast("Select a segment first, then insert the block into it", "info"); return; }
    patch([{ op: "insertBlock", segmentNo: selected.segmentNo, blockCode, blockVersion: 0 }]);
    setBlockPicker(false);
  };

  return (
    <div className="wfa">
      <div style={{ padding: "16px 22px 0" }}>
        <div className="wfa-topbar">
          <button className="wfa-btn ghost" onClick={() => navigate("/workflows")}><i className="ti ti-arrow-left" aria-hidden="true" /> Workflows</button>
          {/* C1 — the template-general editor: the name is editable in place (setTemplate). */}
          {editable
            ? <input className="wfa-h1edit" defaultValue={tpl.description ?? ""} key={`tn${tpl.code}${tpl.description}`} aria-label="Template name"
                onBlur={(e) => e.target.value !== (tpl.description ?? "") && patch([{ op: "setTemplate", description: e.target.value }])} placeholder="Template name" />
            : <h1>{tpl.description || tpl.code}</h1>}
          {/* Version switcher — every version is reachable, so a draft never hides the Active one. */}
          <span className="wfa-vswitch">
            {tpl.kind === "block" && <span className="wfa-vbadge">block</span>}
            {versions.map((x) => (
              <button key={x.version} className={`wfa-vpill ${x.status} ${x.version === version ? "on" : ""}`} onClick={() => goVersion(x.version)} title={`View the ${x.status} version (v${x.version})`}>
                v{x.version} · {x.status}
              </button>
            ))}
            {v && <span className={`wfa-vbadge ${v.valid ? "" : "bad"}`}>{v.valid ? "✓ valid" : `✕ ${v.counts.error} error${v.counts.error > 1 ? "s" : ""}`}</span>}
          </span>
          <div className="wfa-spacer" />
          {editable && <button className="wfa-btn" onClick={() => setBlockPicker(true)}>＋ Insert block</button>}
          {editable && <button className="wfa-btn" disabled={patchMut.isPending} onClick={() => code && patchMut.mutate({ code, version, patches: [] }, { onSuccess: () => showToast(v?.valid ? "Valid — no errors" : `${v?.counts.error ?? 0} error(s) — see findings`, v?.valid ? "success" : "info") })}>Validate</button>}
          {editable && <button className="wfa-btn primary" disabled={publishMut.isPending || !v?.valid} onClick={doPublish}>Publish</button>}
          {editable && <button className="wfa-btn danger" disabled={discardMut.isPending} onClick={doDiscard}>Discard draft</button>}
          {canAuthor && !editable && draftV && version !== draftV && <button className="wfa-btn primary" disabled={newDraftMut.isPending} onClick={() => goVersion(draftV)}>Edit draft v{draftV}</button>}
          {canAuthor && !editable && !draftV && <button className="wfa-btn primary" disabled={newDraftMut.isPending} onClick={doNewDraft}>Open new draft version</button>}
        </div>

        {tpl.source === "derived" && (tpl.derivationReport || tpl.notesProvenance) && <DeriveBanner report={tpl.derivationReport || tpl.notesProvenance || ""} />}

        {v && v.findings.length > 0 && (
          <div className="wfa-findings">
            <button className="wfa-findings__hd" style={{ width: "100%", border: 0, cursor: "pointer" }} onClick={() => setShowFindings((s) => !s)}>
              <i className={`ti ti-chevron-${showFindings ? "down" : "right"}`} aria-hidden="true" />
              {v.valid ? "Valid" : `${v.counts.error} error${v.counts.error > 1 ? "s" : ""}`}{v.counts.warning ? ` · ${v.counts.warning} warning${v.counts.warning > 1 ? "s" : ""}` : ""}{v.counts.info ? ` · ${v.counts.info} info` : ""}
            </button>
            {showFindings && v.findings.map((f, i) => (
              <button key={i} className="wfa-finding" onClick={() => f.segmentNo && setSelected({ kind: "segment", segmentNo: f.segmentNo, stepNo: f.stepNo })}>
                <span className={`sev ${f.severity}`}>{f.severity}</span><span>{f.message}</span>
              </button>
            ))}
          </div>
        )}
        {!canAuthor && (
          <div className="wfa-note" role="status" style={{ marginBottom: 8 }}>
            <b>Authoring needs the workflow-author role.</b> You're viewing this workflow read-only. To design or edit it, ask a workspace owner for the <em>Workflow author</em> role.
          </div>
        )}
        {canAuthor && !editable && <div className="wfa-note" style={{ marginBottom: 8 }}>
          This version is {tpl.status} (read-only). {draftV && version !== draftV
            ? <>A draft (v{draftV}) is already open — <b>Edit draft v{draftV}</b> to continue it, or <b>Discard draft</b> from there to drop it.</>
            : "Open a new draft version to edit."}
        </div>}
      </div>

      <div className="wfa-shell">
        <TemplateCanvas detail={detail} selected={selected} onSelect={setSelected} picked={picked} onPick={(seg, c) => setPicked((p) => ({ ...p, [seg]: c }))} actions={actions} />
        <aside className="wfa-side">{inspectorCtx && <InspectorRail selected={selected} ctx={inspectorCtx} />}</aside>
      </div>

      {blockPicker && <BlockPicker onClose={() => setBlockPicker(false)} onInsert={insertBlock} />}
    </div>
  );
}
